// src/pages/Techniques/TechniquesCard.jsx
import React, { useState } from "react";
import { FaInfoCircle, FaVideo, FaTimes } from "react-icons/fa";
import Button from "../../components/Button/Button";

const getCeintureColor = (ceintureRaw = "") => {
  const c = ceintureRaw.toLowerCase();
  if (c === "jaune") return "bg-yellow-400 text-black";
  if (c === "orange") return "bg-orange-400 text-black";
  if (c === "verte") return "bg-green-400 text-black";
  if (c === "bleue") return "bg-blue-500 text-white";
  if (c === "marron") return "bg-amber-800 text-white";
  if (c === "noire") return "bg-slate-900 text-white";
  return "bg-slate-400 text-black";
};

const TechniquesCard = ({ technique }) => {
  const {
    image1_url,
    nom,
    famille,
    traduction,
    ceinture,
    video_url,
    uv2,
    description,
  } = technique;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openVideoInNewTab = () => {
    if (!video_url) return;
    window.open(video_url, "_blank", "noopener,noreferrer");
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      closeModal();
    }
  };

  const imgSrc = image1_url || "/img/default_image.jpg";

  return (
    <>
      {/* CARD */}
      <article
        className="group relative flex flex-col h-full bg-white dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 
                   rounded-2xl overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-200 cursor-pointer"
        onClick={openModal}
      >
        {/* Image : hauteur fixe + image entière */}
        <div className="relative h-44 sm:h-52 bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
          <img
            src={imgSrc}
            alt={nom}
            className="max-h-full max-w-full object-contain transform group-hover:scale-105 transition-transform duration-500"
          />

          {/* Badge ceinture */}
          {ceinture && (
            <div
              className={`absolute left-2 top-2 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold shadow-md ${getCeintureColor(
                ceinture
              )}`}
            >
              {ceinture}
            </div>
          )}

          {/* Bouton info : bien visible (blanc + contour primaire) */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openModal();
            }}
            className="absolute right-2 top-2 inline-flex items-center justify-center w-7 h-7 rounded-full 
                       bg-white text-primary border border-primary shadow-sm hover:bg-primary hover:text-white transition-colors"
            title="Détails de la technique"
          >
            <FaInfoCircle className="text-xs" />
          </button>

          {/* Vidéo disponible */}
          {video_url && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openVideoInNewTab();
              }}
              className="absolute right-2 bottom-2 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] 
                         bg-black/70 text-white shadow hover:bg-black/90 transition-colors"
              title="Voir la vidéo"
            >
              <FaVideo className="text-xs" />
              <span>Vidéo</span>
            </button>
          )}
        </div>

        {/* Contenu */}
        <div className="flex flex-col flex-grow p-3 sm:p-4 space-y-2">
          <div className="min-h-[48px]">
            <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-50 leading-tight line-clamp-2">
              {nom}
            </h3>
            {traduction && (
              <p className="mt-0.5 text-[11px] sm:text-xs italic text-slate-600 dark:text-slate-300 line-clamp-2">
                {traduction}
              </p>
            )}
          </div>

          {/* Meta : famille + UV2 */}
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {famille && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                {famille}
              </span>
            )}
            {uv2 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium bg-primary/10 text-primary">
                Fiche UV2 : {uv2}
              </span>
            )}
          </div>
        </div>
      </article>

      {/* MODALE DÉTAIL TECHNIQUE */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 px-3 sm:px-4"
          onClick={handleOverlayClick}
        >
          <div
            className="relative max-w-2xl w-full bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermer */}
            <button
              type="button"
              onClick={closeModal}
              className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-full 
                         bg-slate-900/80 text-slate-100 hover:bg-slate-800"
              aria-label="Fermer"
            >
              <FaTimes className="w-4 h-4" />
            </button>

            {/* Image dans la modale : toujours visible en entier */}
            <div
              className="relative bg-black flex items-center justify-center px-2 pt-4 pb-2
             max-h-[70vh] overflow-hidden"
            >
              <img
                src={imgSrc}
                alt={nom}
                className="max-h-[60vh] w-auto object-contain"
              />

              {ceinture && (
                <div
                  className={`absolute left-3 top-3 inline-flex items-center px-3 py-1 rounded-full
                  text-[11px] font-semibold shadow-md ${getCeintureColor(
                    ceinture
                  )}`}
                >
                  {ceinture}
                </div>
              )}

              {/* {video_url && (
                <button
                  type="button"
                  onClick={openVideoInNewTab}
                  className="absolute right-3 bottom-3 inline-flex items-center gap-1 px-3 py-1.5
                 rounded-full text-[11px] bg-primary/90 text-white shadow hover:bg-primary"
                >
                  <FaVideo className="text-xs" />
                  <span>Voir la vidéo</span>
                </button>
              )} */}
            </div>

            {/* Corps */}
            <div className="p-4 sm:p-6 space-y-3">
              <div>
                <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-primary mb-1">
                  Technique de Judo
                </p>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50 leading-tight">
                  {nom}
                </h2>
                {traduction && (
                  <p className="mt-1 text-sm italic text-slate-600 dark:text-slate-300">
                    {traduction}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2 text-xs sm:text-[13px] text-slate-700 dark:text-slate-300">
                {famille && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800/80">
                    Famille : {famille}
                  </span>
                )}
                {uv2 && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800/80">
                    Fiche UV2 : {uv2}
                  </span>
                )}
              </div>

              {description && (
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  {description}
                </p>
              )}

              <div className="flex justify-end gap-2 mt-2">
                {video_url && (
                  <Button
                    text="Voir la vidéo"
                    icon={FaVideo}
                    onClick={openVideoInNewTab}
                    type="button"
                    className="btn-primary gap-2"
                  />
                )}
                <Button
                  text="Fermer"
                  icon={FaTimes}
                  onClick={closeModal}
                  type="button"
                  className="btn-ghost gap-2"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TechniquesCard;
